import { View, Text, StyleSheet, TouchableOpacity, Linking, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useStudentRetention } from '@/hooks/useStudentRetention';
import { CATEGORY_LABEL, CATEGORY_COLOR } from '@/lib/retention';

interface Props {
  studentId: string;
  studentName?: string | null;
  studentPhone?: string | null;
}

function cleanPhone(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10) return null;
  return digits.length === 10 || digits.length === 11 ? `55${digits}` : digits;
}

export function StudentRetentionCard({ studentId, studentName, studentPhone }: Props) {
  const { data, isLoading } = useStudentRetention(studentId);

  if (isLoading || !data) {
    return (
      <View style={[styles.card, { alignItems: 'center', justifyContent: 'center', minHeight: 120 }]}>
        <ActivityIndicator color="#C8102E" />
      </View>
    );
  }

  const { category, daysSinceLast, sessionsThisWeek, sessionsThisMonth, weeklyHistory } = data;
  const catColor = CATEGORY_COLOR[category];

  function openWhatsApp() {
    const phone = cleanPhone(studentPhone);
    if (!phone) return;
    const firstName = (studentName ?? 'aluno').split(' ')[0];
    const msg = daysSinceLast == null
      ? `Oi ${firstName}! Bora começar a treinar? Tô aqui pra te ajudar.`
      : `Oi ${firstName}! Vi que faz ${daysSinceLast} dias sem treinar. Tá tudo bem? Me conta como posso te ajudar.`;
    Linking.openURL(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`).catch(() => {});
  }

  const phoneOk = !!cleanPhone(studentPhone);
  const maxSparkValue = Math.max(1, ...weeklyHistory.map(w => w.count));

  return (
    <View style={[styles.card, { borderLeftColor: catColor }]}>

      {/* Topo: badge + ação */}
      <View style={styles.headerRow}>
        <View style={[styles.badge, { backgroundColor: catColor + '22' }]}>
          <Text style={[styles.badgeText, { color: catColor }]}>{CATEGORY_LABEL[category]}</Text>
        </View>

        <TouchableOpacity
          style={[styles.notifyBtn, !phoneOk && styles.notifyBtnDisabled]}
          onPress={openWhatsApp}
          disabled={!phoneOk}
          activeOpacity={0.7}
        >
          <Ionicons name="logo-whatsapp" size={14} color={phoneOk ? '#fff' : '#aaa'} />
          <Text style={[styles.notifyBtnText, !phoneOk && { color: '#aaa' }]}>Notificar</Text>
        </TouchableOpacity>
      </View>

      {/* Stats */}
      <View style={styles.statsRow}>
        <Stat label="dias sem treinar" value={daysSinceLast == null ? '—' : String(daysSinceLast)} />
        <View style={styles.statDivider} />
        <Stat label="esta semana" value={String(sessionsThisWeek)} />
        <View style={styles.statDivider} />
        <Stat label="este mês" value={String(sessionsThisMonth)} />
      </View>

      {/* Sparkline */}
      <View style={styles.sparkBlock}>
        <Text style={styles.sparkLabel}>Últimas 8 semanas</Text>
        <View style={styles.sparkRow}>
          {weeklyHistory.map((w, i) => {
            const h = Math.max(2, (w.count / maxSparkValue) * 28);
            return (
              <View key={i} style={styles.sparkColumn}>
                <View style={[styles.sparkBar, { height: h, backgroundColor: catColor }]} />
                <Text style={styles.sparkCount}>{w.count}</Text>
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.statBox}>
      <Text style={styles.statValue}>{value}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 14,
    marginBottom: 16,
    borderLeftWidth: 4,
    shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 4, shadowOffset: { width: 0, height: 1 },
    elevation: 1,
  },

  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  badge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 12 },
  badgeText: { fontSize: 12, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },

  notifyBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: '#25D366', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8,
  },
  notifyBtnDisabled: { backgroundColor: '#EEE' },
  notifyBtnText: { color: '#fff', fontSize: 12, fontWeight: '700' },

  statsRow: { flexDirection: 'row', alignItems: 'center', marginTop: 14, marginBottom: 12 },
  statBox: { flex: 1, alignItems: 'center' },
  statValue: { fontSize: 22, fontWeight: '800', color: '#1A1A1A' },
  statLabel: { fontSize: 10, color: '#888', marginTop: 2, textAlign: 'center' },
  statDivider: { width: 1, height: 28, backgroundColor: '#EEE' },

  sparkBlock: { borderTopWidth: 1, borderTopColor: '#F2F2F2', paddingTop: 10 },
  sparkLabel: { fontSize: 10, color: '#888', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 },
  sparkRow: { flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', height: 40 },
  sparkColumn: { flex: 1, alignItems: 'center', justifyContent: 'flex-end', gap: 2 },
  sparkBar: { width: 14, borderRadius: 3 },
  sparkCount: { fontSize: 9, color: '#aaa', marginTop: 1 },
});
